import type { ActionDef } from "@ozpanel/kit";
import type { Backend } from "../../accountsApi";

// appActions — one declaration shared by the Apps list (row menu) and the app detail page.
// Danger levels mirror the backend's own: start/restart/relink/enable are medium, and
// stop/disable/delete are high (a stop takes a customer's site down; a disable does it
// cluster-wide and journalled).
//
// enable/disable and start/stop are deliberately BOTH offered and labelled distinctly:
// start/stop are transient on the placement node, enable/disable are desired state that
// survives a reconcile. Collapsing them into one control would silently pick one meaning.
type Api = ReturnType<typeof import("../../accountsApi").useAccountsApi>;

export function appActions(api: Api, opts: { onDeleted?: () => void } = {}): ActionDef<Backend>[] {
  const invalidate = (b: Backend) => [["apps"], ["app", b.name]];

  return [
    {
      key: "start",
      label: "Start",
      run: (b) => api.startApp(b.name),
      invalidates: invalidate,
      successMessage: (b) => `${b.name} started`,
    },
    {
      key: "restart",
      label: "Restart",
      run: (b) => api.restartApp(b.name),
      invalidates: invalidate,
      successMessage: (b) => `${b.name} restarted`,
    },
    {
      key: "stop",
      label: "Stop",
      danger: "high",
      confirm: (b) => ({
        title: `Stop ${b.name}?`,
        body: (
          <p>
            Whatever this app serves at <strong>{b.domain}</strong> stops responding until it is
            started again. This is a transient stop on the placement node — it does not change
            whether the app is enabled.
          </p>
        ),
        confirmLabel: "Stop",
      }),
      run: (b) => api.stopApp(b.name),
      invalidates: invalidate,
      successMessage: (b) => `${b.name} stopped`,
    },
    {
      key: "enable",
      label: "Enable",
      available: (b) => !b.enabled,
      run: (b) => api.enableApp(b.name),
      invalidates: invalidate,
      successMessage: (b) => `${b.name} enabled`,
    },
    {
      key: "disable",
      label: "Disable",
      danger: "high",
      available: (b) => b.enabled,
      confirm: (b) => ({
        title: `Disable ${b.name}?`,
        body: (
          <p>
            Recorded cluster-wide as desired-off, so no node will start it again until it is
            re-enabled — unlike Stop, this survives a reconcile.
          </p>
        ),
        confirmLabel: "Disable",
      }),
      run: (b) => api.disableApp(b.name),
      invalidates: invalidate,
      successMessage: (b) => `${b.name} disabled`,
    },
    {
      key: "relink",
      label: "Fix routing",
      // Exists because Docker can reassign a container address on restart, leaving Caddy
      // pointed at the old one. Surfacing it matters: the symptom is a site serving the
      // WRONG app with HTTP 200, which no status indicator reveals.
      run: (b) => api.relinkApp(b.name),
      invalidates: invalidate,
      successMessage: (b) => `${b.name} re-bound to its current container address`,
    },
    {
      key: "delete",
      label: "Delete",
      danger: "high",
      confirm: (b) => ({
        title: `Delete ${b.name}?`,
        body: (
          <p>
            Stops the app and removes its unit from every node it is placed on. Its route at{" "}
            <strong>{b.domain}</strong> stops serving. This cannot be undone.
          </p>
        ),
        confirmLabel: "Delete permanently",
      }),
      run: (b) => api.deleteApp(b.name),
      invalidates: () => [["apps"]],
      successMessage: (b) => `${b.name} deleted`,
      onDone: () => opts.onDeleted?.(),
    },
  ];
}
